import { Save } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useProject } from "@/features/project";
import { HighlightedTextarea } from "./HighlightedTextarea";

export const StateEditor: React.FC = () => {
  const { updateProject, chatState, messages, files, data } = useProject();

  const [jsonContent, setJsonContent] = useState("");
  const [initialJsonContent, setInitialJsonContent] = useState("");

  const isDirty = jsonContent !== initialJsonContent;

  const contextJson = useMemo(
    () => JSON.stringify({ chatState, messages, files, data }, null, 2),
    [chatState, messages, files, data],
  );

  useEffect(() => {
    if (isDirty) return;
    setJsonContent(contextJson);
    setInitialJsonContent(contextJson);
  }, [contextJson, isDirty]);

  const handleSave = useCallback(async () => {
    const parsed = JSON.parse(jsonContent);

    updateProject({
      chatState: parsed.chatState,
      messages: parsed.messages,
      files: parsed.files,
      data: parsed.data,
    });

    setInitialJsonContent(jsonContent);
  }, [jsonContent, updateProject]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (isDirty) {
          handleSave();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDirty, handleSave]);

  return (
    <div className="h-full w-full bg-slate-900 border-r border-slate-800 font-mono text-sm overflow-hidden relative">
      <div className="h-full overflow-auto custom-scrollbar">
        {jsonContent === "" ? (
          <div className="h-full flex items-center justify-center text-slate-500">Loading...</div>
        ) : (
          <div className="min-w-fit">
            <HighlightedTextarea code={jsonContent} onChange={setJsonContent} language="json" />
          </div>
        )}

        <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
          {isDirty && (
            <button
              type="button"
              onClick={handleSave}
              className="p-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded shadow-lg transition-all opacity-80 hover:opacity-100"
              title="Save changes"
            >
              <Save size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
